import {
  useLazyQuery,
  useMyUrl,
  useQuery,
  useTranslation,
} from '@gimlite/common';
import {
  Alert,
  Button,
  Filter,
  Icon,
  Page,
  Row,
  Searchbar,
  Sort,
  Tab,
  TabGroup,
  Table,
  Text,
  Zone,
  ZoneArea,
} from '@gimlite/ui-v2';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useReducer } from 'react';
import {
  Query,
  QueryCsvSitesArgs,
  QuerySitesArgs,
} from '../../../../client/graphql';
import { EquipmentDetails } from '../components/equipment.read';
import { csvSitesGql } from '../gql/csvSites.gql';
import { sitesGql } from '../gql/sites.gql';

type Status = 'down' | 'up' | 'degraded' | 'not-responding';

const orderStatus = ['down', 'not-responding', 'degraded', 'up'];

export const EquipmentPage = observer(() => {
  const { setParamsUrl, getParamsUrl } = useMyUrl<{
    view: 'smallCard' | 'card' | 'list' | 'map';
    fastFilter: Status;
  }>();

  const { lang } = useTranslation();
  const [openDetails, setOpenDetails] = useReducer(
    (
      state: { id: string | null; isOpen: boolean },
      action: { type: 'OPEN' | 'CLOSE'; id?: string | null },
    ) => {
      switch (action.type) {
        case 'OPEN':
          return { id: action.id ?? null, isOpen: true };
        case 'CLOSE':
          return { id: null, isOpen: false };
        default:
          return state;
      }
    },
    { id: null, isOpen: false },
  );

  const sitesState = useQuery<{ sites: Query['sites'] }, QuerySitesArgs>(
    sitesGql,
    {
      variables: {
        limit: 300,
      },
      pollInterval: 1000 * 60,
    },
  );

  const [csvSitesCall, csvSitesState] = useLazyQuery<
    { csvSites: Query['csvSites'] },
    QueryCsvSitesArgs
  >(csvSitesGql, {
    notification: {
      success: 'The CSV file is being downloaded',
      error: 'Your CSV export failed',
    },
  });

  const sites = useMemo(() => {
    if (!sitesState.data?.sites) return undefined;

    const { list, paging } = sitesState.data.sites;

    return {
      list: list.map((values) => {
        const status = values.monitoring?.status || 'not-responding';
        const alarms = values?.monitoring?.currentAlarms || [];
        const lastPing = values?.monitoring?.lastPing
          ? new Date(values?.monitoring?.lastPing)
          : new Date(0);
        const address = `${values.address1 || '-'}${
          values.city ? `, ${values.city}` : ''
        }`;

        return {
          ...values,
          status: status || 'not-responding',
          name: values?.name || '-',
          alias: values?.alias || '-',
          site: address,
          alert: status || 'not-responding',
          alarms: (alarms || []).length,
          transmission: status === 'not-responding' ? lastPing : 0,
          intervention:
            alarms?.find((value: any) =>
              ['down', 'degraded', 'action-requested'].includes(value?.effect),
            ) || status === 'not-responding'
              ? 'Oui'
              : 'Non',
          batterie: alarms?.find(
            (value: any) => value?.alarm === 'mcu.Battery.Empty',
          )
            ? 'Vide'
            : 'OK',
          ticket: alarms?.find(
            (value: any) => value?.alarm === 'printer.LowPaper',
          )
            ? 'Presque vide'
            : 'OK',
          tirelire: alarms?.find(
            (value: any) => value?.alarm === 'coin.SafeBox.Full',
          )
            ? 'Pleine'
            : alarms?.find(
                  (value: any) => value?.alarm === 'coin.SafeBox.AlmostFull',
                )
              ? 'Presque pleine'
              : 'OK',
        };
      }),
      paging,
    };
  }, [sitesState.data]);

  const filterSearch = useMemo(() => {
    return sites?.list
      ?.sort(
        (a, b) =>
          (b?.monitoring?.status
            ? orderStatus.indexOf(b?.monitoring?.status)
            : 0) -
          (a?.monitoring?.status
            ? orderStatus.indexOf(a?.monitoring?.status)
            : 0),
      )
      .filter((site: any) =>
        getParamsUrl.fastFilter === 'down'
          ? ['down', 'not-responding'].includes(site.status)
          : getParamsUrl.fastFilter === 'degraded'
            ? ['degraded'].includes(site.status)
            : getParamsUrl.fastFilter === 'up'
              ? ['up'].includes(site.status)
              : true,
      );
  }, [sites?.list, getParamsUrl.fastFilter]);

  useEffect(() => {
    if (csvSitesState.data) {
      window.open(csvSitesState.data.csvSites.downloadURL, '_blank');
    }
  }, [csvSitesState.data]);

  type TYPE_DATA = {
    id: string;
    name: string;
    site: string;
    alarms: number | null;
    transmission: number | Date | null;
    intervention: string;
    batterie: string;
    ticket: string;
    tirelire: string;
    alert: string;
    alias: string | null;
    onClick?: (id: string) => void;
  };

  return (
    <Page
      title="Tableau de bord des horodateurs"
      subTitle="Module des équipements"
    >
      <EquipmentDetails
        onClose={() => setOpenDetails({ type: 'CLOSE' })}
        id={openDetails.id}
        isOpen={openDetails.isOpen}
      />
      <Zone
        zones={[['filter'], ['tabs'], ['details']]}
        rows={['min-content', 'min-content', '1fr']}
        columns={['1fr']}
      >
        <ZoneArea area="filter">
          <Row
            width="max-content"
            alignItems="center"
            justifyContent="space-between"
          >
            <Row width="max-content" alignItems="center">
              <Searchbar disabled />
              <Filter disabled />
              <Sort disabled />
            </Row>
            <Row width="min-content" alignItems="center" gap={16}>
              <Button
                size="small"
                iconLeft="export"
                onClick={async () =>
                  await csvSitesCall({
                    variables: {
                      status: getParamsUrl?.fastFilter,
                      targetTZ:
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                    },
                  })
                }
              >
                Exporter (CSV)
              </Button>
            </Row>
          </Row>
        </ZoneArea>

        <ZoneArea area="tabs">
          <Row
            width="max-content"
            alignItems="center"
            justifyContent="space-between"
          >
            <TabGroup>
              <Tab
                selected={!getParamsUrl.fastFilter}
                counter={sites?.list.length}
                onClick={() =>
                  setParamsUrl((prev) => ({ ...prev, fastFilter: undefined }))
                }
              >
                Tous
              </Tab>
              <Tab
                selected={getParamsUrl.fastFilter === 'down'}
                counter={
                  sites?.list?.filter((value: any) =>
                    ['down', 'not-responding'].includes(value?.status),
                  ).length
                }
                onClick={() =>
                  setParamsUrl((prev) => ({
                    ...prev,
                    fastFilter: 'down',
                  }))
                }
              >
                Hors service
              </Tab>
              <Tab
                selected={getParamsUrl.fastFilter === 'degraded'}
                counter={
                  sites?.list?.filter((value: any) =>
                    ['degraded'].includes(value?.status),
                  ).length
                }
                onClick={() =>
                  setParamsUrl((prev) => ({ ...prev, fastFilter: 'degraded' }))
                }
              >
                Dégradés
              </Tab>
              <Tab
                selected={getParamsUrl.fastFilter === 'up'}
                counter={
                  sites?.list?.filter((value: any) =>
                    ['up'].includes(value?.status),
                  ).length
                }
                onClick={() =>
                  setParamsUrl((prev) => ({ ...prev, fastFilter: 'up' }))
                }
              >
                En marche
              </Tab>
            </TabGroup>
            <TabGroup>
              <Tab
                disabled={true}
                icon="card2"
                onClick={() =>
                  setParamsUrl((prev) => ({ ...prev, view: 'smallCard' }))
                }
                selected={getParamsUrl.view === 'smallCard'}
              ></Tab>
              <Tab
                disabled={true}
                icon="grid"
                onClick={() =>
                  setParamsUrl((prev) => ({ ...prev, view: 'card' }))
                }
                selected={getParamsUrl.view === 'card'}
              ></Tab>
              <Tab
                icon="list"
                onClick={() =>
                  setParamsUrl((prev) => ({ ...prev, view: 'list' }))
                }
                selected={!getParamsUrl.view || getParamsUrl.view === 'list'}
              ></Tab>
              <Tab
                disabled={true}
                icon="map"
                onClick={() =>
                  setParamsUrl((prev) => ({ ...prev, view: 'map' }))
                }
                selected={getParamsUrl.view === 'map'}
              ></Tab>
            </TabGroup>
          </Row>
        </ZoneArea>

        <ZoneArea area="details">
          <Table<TYPE_DATA>
            data={
              filterSearch?.map((value) => ({
                ...value,
                id: value._id!,
                onClick: () => {
                  setOpenDetails({ type: 'OPEN', id: value._id! });
                },
              })) || []
            }
            columns={[
              {
                header: 'Automate',
                accessorKey: 'alias',
              },
              {
                header: 'Statut',
                accessorKey: 'alert',
                cell: (ctx) => {
                  const value = ctx.getValue();

                  switch (value) {
                    case 'not-responding':
                    case 'down':
                      return <Alert variant="alert_error">Hors service</Alert>;
                    case 'degraded':
                      return <Alert variant="alert_warning">Dégradé</Alert>;
                    case 'up':
                      return <Alert variant="alert_success">En service</Alert>;
                    default:
                      return null;
                  }
                },
              },
              {
                header: 'Adresse',
                accessorKey: 'site',
              },
              {
                header: 'Alarmes',
                accessorKey: 'alarms',
              },
              {
                header: 'Transmission',
                accessorKey: 'transmission',
                cell: (ctx) => {
                  const value = ctx.getValue();

                  return (
                    <Row gap={4} justifyContent="center" alignItems="center">
                      <Icon
                        variant="transmission"
                        color={value ? 'alert_error' : 'alert_success'}
                      ></Icon>
                      <Text
                        variant="caption"
                        color="deep_indigo"
                        whiteSpace="nowrap"
                      >
                        {value
                          ? `Pas de transmission depuis ${DateTime.fromISO(
                              new Date(value).toISOString(),
                            )
                              .toRelative({
                                locale: 'fr',
                                unit: 'days',
                              })
                              ?.replace('il y a', '')}`
                          : 'OK'}
                      </Text>
                    </Row>
                  );
                },
              },
              {
                header: "Besoin d'intervention",
                accessorKey: 'intervention',
              },
              {
                header: 'Batterie',
                accessorKey: 'batterie',
              },
              {
                header: 'Papier',
                accessorKey: 'ticket',
              },
              {
                header: 'Tirelire',
                accessorKey: 'tirelire',
                maxWidth: 40,
              },
            ]}
          ></Table>
        </ZoneArea>
      </Zone>
    </Page>
  );
});
